import cn from 'classnames';
import { useRouter } from 'next/router';
import MissingSomethingModalButton from '@/creditflowresearch/ui/common/components/MissingSomethingModalButton';
import { ITabTypeItem } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/ITabItem';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import ArticlesContextProvider from '@/creditflowresearch/application/ArticlesAndStats/Articles/CreditFlowArticlesContext';
import ArticlesFilterContextProvider
  from '@/creditflowresearch/application/ArticlesAndStats/Articles/Filter/ArticlesFilterContext';
import { IMarketArticlesData } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IMarketArticle';
import ArticlesMarketThemesContextProvider from '@/creditflowresearch/application/ArticlesAndStats/ArticlesMarketThemesContext';
import CheckUserAccess from '@/creditflowresearch/ui/common/components/CheckUserAccess';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import NoAccessOverlayArticles from '@/creditflowresearch/ui/components/creditFlow/common/NoAccessOverlayArticles';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import ArticlesList from './ArticlesList';
import ArticlesTypesTabs from './ArticlesTypesTabs';
import { getTypeTabsConfig } from './helpers';
import FiltersButton from './FiltersButton';
import PageMeta from '@/creditflowresearch/ui/common/components/PageMeta';
import getArticlesMetaSchema from './getArticlesMetaSchema';
import { UserPlanFeature } from '@creditflowresearch/user-plan/vo/IUserPlanResponse';
import ProductPageHeader from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader';
import { useBreadcrumbs } from '@/creditflowresearch/ui/common/hooks';
import getArticlesBreadcrumbs from '@/creditflowresearch/ui/components/creditFlow/articles/getArticlesBreadcrumbs';
import { useCorporateMarketFilterToggle } from '@/creditflowresearch/ui/common/hooks/filters/useCorporateMarketFilterToggle';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';
import { IWithArticlesConfigProps } from './withArticlesConfig';

import pageStyles from '@framework/global-styles/page.module.scss';
import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';
import styles from './articles.module.scss';

interface IArticlesBaseProps {
  articlesData: IMarketArticlesData,
}

interface IArticlesProps extends IArticlesBaseProps, IWithArticlesConfigProps {}

const Articles = ({ articlesData, articlesConfig }: IArticlesProps) => {
  const { query, asPath } = useRouter();
  const { handleFilterToggle } = useCorporateMarketFilterToggle();

  const onToggle = articlesConfig.needsSegmentValidation
    ? (searchQuery: string) => handleFilterToggle(searchQuery)
    : undefined;

  const { articleType, products, regions, markets, segments } = query;
  const tabs = getTypeTabsConfig();
  const {
    checkAccessByUserMarket,
    checkAccessByUserProducts,
  } = usePageFiltersContext();
  const title = articlesConfig.title || 'Commentary & Deal Flow';
  const titleWithPrefix = articlesConfig.titlePrefix + title;
  const {
    checkAccessByUserPlan,
    checkAccessByFeature,
  } = useUserPlanContext();

  const granted = useAccessGrant();

  const checks = [
    granted || checkAccessByUserPlan(UserPlanType.PREMIUM),
    granted || checkAccessByUserMarket(),
    granted || checkAccessByUserProducts(),
    granted || checkAccessByFeature(UserPlanFeature.CommentaryAndDealFlow),
  ];

  useBreadcrumbs(
    () => getArticlesBreadcrumbs(query),
    [query],
  );

  return (
    <>
      <PageMeta
        title={ titleWithPrefix }
        schema={ getArticlesMetaSchema({
          pathname: asPath,
          products: products as string,
          regions: regions as string,
          markets: markets as string,
          segments: segments as string,
        }) }
      />
      <ProductPageHeader
        title={ title }
        items={ articlesConfig.headerFilters }
        onToggle={ onToggle }
        accessConditions={ [{ checks }] }
        blockingAccessConditions={ [{ checks: [granted || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
        panelBackground={ articlesConfig.panelBackground }
      />
      <CheckUserAccess conditions={ [{ checks, fallback: <NoAccessOverlayArticles /> }] }>
        <ArticlesMarketThemesContextProvider>
          <ArticlesFilterContextProvider
            filtersState={ articlesData.payload.filter }
            filtersPayload={ articlesData.payload.filter }
            hasFilters={ articlesConfig.showFiltersButton }
          >
            <div className={ styles.articlesTopBarWrapper }>
              <div className={ pageStyles.pageNavigationLinks } data-test="articleTypesTabs">
                <ArticlesTypesTabs<ITabTypeItem>
                  tabs={ tabs }
                  basePath={ articlesConfig.tabsBasePath }
                  activeClassName={ pageStyles.isActiveNavLink }
                  onIsActive={ ({ baseUrl }) => `/${ articleType }` === baseUrl }
                />
              </div>
            </div>

            <ArticlesContextProvider articlesData={ articlesData }>
              <ArticlesList
                columns={ articlesConfig.columns }
                title={ articlesConfig.title }
                titlePrefix={ articlesConfig.titlePrefix }
                tabsBasePath={ articlesConfig.tabsBasePath }
                filtersButton={
                articlesConfig.showFiltersButton ?
                  <FiltersButton className={ styles.mobileFullWidth } />
                  : undefined
                }
              />
            </ArticlesContextProvider>
          </ArticlesFilterContextProvider>
        </ArticlesMarketThemesContextProvider>
      </CheckUserAccess>

      <MissingSomethingModalButton className={ cn(spacesStyles.ptxl, spacesStyles.pbxl) } />
    </>
  );
};

export default Articles;
